import { create } from 'zustand';
import type { MediaKind, MediaSortDir, MediaSortKey, MediaTab } from '../lib/attachment-groups';

/**
 * chatMediaStore (chat-media-date-groups — 2026-08-07) — 우측 사이드바 미디어 브라우저의
 * 탭·정렬·종류 필터 상태. localStorage 로 영속(방 전환·새로고침에도 유지).
 *
 * additive — [[chatSidebarStore]] 는 접힘/폭만 담당한다는 docblock 스코프를 지키기 위해 확장하지 않고
 * 별도 store 를 둔다. 기존 selector 에 영향 0.
 *
 * 검색어·펼침 집합·라이트박스 인덱스는 **컴포넌트 로컬**이다 — 방 단위로 리셋돼야 하고 금방 낡는다.
 */

const LS_TAB = 'chat.media.tab';
const LS_SORT_KEY = 'chat.media.sort_key';
const LS_SORT_DIR = 'chat.media.sort_dir';
const LS_KIND = 'chat.media.kind';

const TABS: readonly MediaTab[] = ['files', 'images'];
const SORT_KEYS: readonly MediaSortKey[] = ['date', 'name', 'size'];
const SORT_DIRS: readonly MediaSortDir[] = ['desc', 'asc'];
const KINDS: readonly MediaKind[] = ['all', 'doc', 'sheet', 'archive', 'etc'];

/** 저장값이 허용 union 안일 때만 채택 — 손상/구버전 값이면 기본값으로 자연 폴백. */
function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

function persist(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* localStorage 비활성(프라이빗 모드 등) — 무시, 세션 내 동작은 유지 */
  }
}

interface ChatMediaState {
  tab: MediaTab;
  sortKey: MediaSortKey;
  sortDir: MediaSortDir;
  kind: MediaKind;
  setTab: (tab: MediaTab) => void;
  /** 같은 키를 다시 누르면 방향 토글, 다른 키면 그 키의 자연스러운 기본 방향으로 전환. */
  toggleSort: (key: MediaSortKey) => void;
  setKind: (kind: MediaKind) => void;
}

/**
 * 축마다 "처음 눌렀을 때 기대되는 방향" 이 다르다 —
 * 날짜·크기는 큰 쪽(최신·큰 파일)부터, 이름은 가나다순(오름차순)이 자연스럽다.
 */
export const DEFAULT_SORT_DIR: Record<MediaSortKey, MediaSortDir> = {
  date: 'desc',
  name: 'asc',
  size: 'desc',
};

export const useChatMediaStore = create<ChatMediaState>((set, get) => ({
  tab: read(LS_TAB, TABS, 'files'),
  sortKey: read(LS_SORT_KEY, SORT_KEYS, 'date'),
  sortDir: read(LS_SORT_DIR, SORT_DIRS, 'desc'),
  kind: read(LS_KIND, KINDS, 'all'),

  setTab: (tab) => {
    persist(LS_TAB, tab);
    set({ tab });
  },

  toggleSort: (key) => {
    const { sortKey, sortDir } = get();
    const nextDir: MediaSortDir =
      sortKey === key ? (sortDir === 'desc' ? 'asc' : 'desc') : DEFAULT_SORT_DIR[key];
    persist(LS_SORT_KEY, key);
    persist(LS_SORT_DIR, nextDir);
    set({ sortKey: key, sortDir: nextDir });
  },

  setKind: (kind) => {
    persist(LS_KIND, kind);
    set({ kind });
  },
}));
